/**
 * About tab — "What's measured" (PLT-5714).
 *
 * Folds the rich category explanation that used to live only in the
 * standalone `ImpactExplanationModal` into the drill-in panel, so the
 * "what does this impact category cover?" answer lives inside the same
 * panel the rest of the row opens (Janne's one-drill-down constraint).
 * The category label click routes here (see `impact-titles/index.tsx`),
 * preserving the old "click the text = learn what it means" behaviour.
 *
 * Content, NOT chrome, is shared with the legacy modal. The tab reads
 * the same source-of-truth data the modal reads — `uiImpactCategoryBy
 * Code[...].explanations` for topics/examples and `allImpactCategory
 * DataSources` for the basis-for-calculation links — so the two surfaces
 * can never drift on what they say.
 *
 * Sections: Main topics considered, Basis for calculation, and Examples
 * of positive & negative impacts. There is intentionally NO "More
 * information" KB-links section — it was dropped as not useful in this
 * in-panel context (Janne review, Aug 2026).
 *
 * Documentation framing (Janne review, Aug 2026): every section here is
 * definitional — it describes the impact CATEGORY, identically for every
 * company, not the company the panel was opened for. Rather than a loud
 * banner up top (an earlier cut had an eyebrow + a company-naming
 * sentence, which read as too verbose and was pulled), the scope point is
 * now carried quietly by each section's own one-line description ("… when
 * calculating {label} for a company") plus the examples disclaimer. So
 * there is no standalone "About …" line at the top any more.
 *
 * Presentation is documentation-first, deliberately its OWN language
 * rather than the sibling tabs' `DrillInSectionHeader`: section headers
 * are bold, no underline rule, with a muted description line beneath.
 * Basis for calculation renders as a BORDERED card family (Janne review,
 * Aug 2026, after comparing a borderless divider list against bordered
 * cards): a bordered card in a two-column grid — a tinted icon chip
 * stacked above the title, no trailing arrow, so each card fills its
 * vertical space and the two columns read compactly. On hover a link
 * card's border turns blue and its background tints faintly (and stays
 * underline-free — the card is the click target, not link text).
 *   - Basis for calculation — ONE card per individual source (not per
 *     group), so a category whose groups are lopsided (e.g. GHG: one
 *     scientific-article source vs. several databases) still reads evenly.
 *     The group name becomes a small caption under the source. Each source
 *     picks a glyph from `SOURCE_ICON` (falling back to its group's glyph)
 *     so a grid of sources looks varied rather than a repeated icon
 *     (Janne review: "more variation in the icons").
 * Topics render in one of two shapes, chosen from the CONTENT rather than
 * hard-coded per category (Janne review, Aug 2026). Most categories list
 * short keyword tags ("Carbon dioxide", "VAT", "Habitat loss") → light-blue
 * chips with a capped (non-pill) radius. But a handful stuff prose into the
 * same field — a single full sentence (Jobs), a multi-sentence paragraph
 * (Scarce human capital), or a long descriptive clause (Waste, Scarce
 * natural resources) — where a chip is simply the wrong container no matter
 * its radius. When any item in a category reads as prose (see
 * `looksLikeProse`), the whole list renders as a plain paragraph (single
 * item) or short bulleted sentences (several) instead of chips. This is a
 * shape heuristic, not a per-name special case, so new prose categories are
 * handled automatically. The real root cause is that `topicsConsidered`
 * mixes tags and prose in the source data — the durable fix is a data
 * cleanup by the impact team; this keeps the UI robust meanwhile.
 *
 * The positive/negative examples keep the sibling tabs' row vocabulary (a
 * 32px valence-tinted icon square + text), NOT a card — those rows are
 * shared with the Everyday-units tab and stay consistent with it. Note
 * the square's glyphs here (`CheckStreamlineCoreLine`, `XCircle`) are
 * stroke-only line icons, so the square must NOT force `fill` on them —
 * filling the open checkmark path renders it as a solid blob (the "broken
 * plus" Janne flagged, Aug 2026). It draws them with `stroke` only.
 *
 * The one-line `productViewExplanation` definition is NOT repeated here
 * — the panel header already renders it above the stat row (see
 * `panel.tsx`), matching the modal's own rule of only showing the
 * standalone Definition block for product-type entities.
 */
import {
  allImpactCategoryDataSources,
  DataSourceGroup,
  ImpactCategoryDataSource,
} from '@upright/common/model/input/data-sources/impact-category-data-sources';
import { uiImpactCategoryByCode } from '@upright/common/model/input/impacts';
import { toImpactCategoryId } from '@upright/common/model/input/impacts/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import { Valence } from '@upright/common/model/types';
import {
  Book1StreamlineCore,
  CheckStreamlineCoreLine,
  ContentStatistic,
  Earth,
  FileDocumentInfoQuickReferenceStreamlineCore,
  FileWithLines,
  GovernmentBuilding,
  GraphBarHorizontal,
  Health,
  JusticeScale,
  Knowledge,
  LayoutGrid,
  LightbulbOn,
  OrganicFlask,
  RecycleBin2StreamlineCore,
  XCircle,
} from '@upright/ui/icons';
import { groupBy } from 'lodash';
import { ComponentType, SVGProps } from 'react';
import styled from 'styled-components';
import { NetImpactDrillInPayload } from '../state';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

interface Props {
  payload: NetImpactDrillInPayload;
  /** Friendly impact-category name (e.g. "GHG emissions") — woven into the
   *  section descriptions so the reader sees the tab is about the
   *  category, not the drilled-in company. */
  impactLabel: string;
  /** Name of the company the panel was opened for — named only in the
   *  examples disclaimer, to say those rows are NOT its figures. */
  entityName: string;
}

/** Fallback glyph per data-source group, used when a source has no more
 *  specific icon in `SOURCE_ICON` below. All are stroked `currentColor`
 *  line icons (Streamline Core), tinted by the card's icon chip. */
const GROUP_ICON: Record<DataSourceGroup, IconType> = {
  [DataSourceGroup.SCIENTIFIC_ARTICLE]: Book1StreamlineCore,
  [DataSourceGroup.DATABASE]: LayoutGrid,
  [DataSourceGroup.PUBLIC]: Earth,
  [DataSourceGroup.CRITICAL_INFRASTRUCTURE]: GovernmentBuilding,
  [DataSourceGroup.COMPANY_REPORT]: FileWithLines,
  [DataSourceGroup.REGULATORY]: JusticeScale,
};

/** Per-source glyph, keyed by the source's `name`. Without this every
 *  "Databases" source would show the same grid icon (a category like GHG
 *  emissions draws on 4–5 database sources), which reads as no variation at
 *  all (Janne review, Aug 2026). Here each source gets an icon that hints
 *  at what it is about — climate, health, materials, statistics, … — so a
 *  grid of source cards looks varied. Anything not listed falls back to
 *  its group icon above, so new sources still render sensibly. All glyphs
 *  are stroked Streamline Core line icons to match the group icons. */
const SOURCE_ICON: Record<string, IconType> = {
  'CORE database': Book1StreamlineCore,
  'OECD PIAAC database': Knowledge,
  'OECD SDBS database': GraphBarHorizontal,
  'IPCC lifecycle CO2 equivalent for electricity production': LightbulbOn,
  'EU criticality levels of raw materials': OrganicFlask,
  'Our World In Data death rates from energy sources': Health,
  'Our World In Data environmental impacts of food': OrganicFlask,
  'UK GHG conversion factors': ContentStatistic,
  'European commission GHG default emissions from biofuels': Earth,
  'Global Burden of Diseases study': Health,
  'CNF nutritional values': OrganicFlask,
  'WHO essential medicines': Health,
  Seafoodwatch: Earth,
  'Product lifecycle assessments': RecycleBin2StreamlineCore,
  'Environmental product declarations': FileDocumentInfoQuickReferenceStreamlineCore,
  'Our World In Data land use': Earth,
};

export function AboutTab({ payload, impactLabel, entityName }: Props) {
  // The About tab is per impact category — the panel hides it at
  // dimension grain (same rule as the Everyday-units tab), since a
  // dimension aggregates ~5 categories with no single explanation.
  // This guard is defensive only.
  if (payload.target.grain !== 'impact') return null;
  // `impactCode` is a valid `ImpactCategoryId`, so it is always a key of
  // the canonical `uiImpactCategoryByCode` map — accessed directly here,
  // matching the sibling lookup in `state.ts`.
  const category = uiImpactCategoryByCode[payload.target.impactCode];
  const { explanations } = category;
  const { topicsConsidered, explanationsAndExamplesByValence: examples } = explanations;
  // Use the label in its display case (e.g. "GHG emissions", "Jobs") — do
  // NOT lowercase it: several category names lead with an acronym that
  // reads wrong lowercased ("ghg emissions").
  const label = impactLabel;
  // Opened from a product profile (PLT-7045), the score being explained is a product's. The
  // examples stay company examples, which is what the methodology writes them as.
  const isProduct = payload.entity.type === ModelEntityType.PRODUCT;

  // Cluster by group so like sources sit together, then flatten to one
  // flat list — each source becomes its own card (Janne review, Aug 2026),
  // which evens out lopsided groups (e.g. GHG's single article source vs.
  // several databases).
  const sources = Object.values(
    groupBy(
      allImpactCategoryDataSources.filter(s => s.impactCategories.includes(toImpactCategoryId(category.code))),
      s => s.group,
    ),
  ).flat();

  return (
    <Root>
      {topicsConsidered && topicsConsidered.length > 0 && (
        <Section>
          <SectionHead
            title="Main topics considered"
            description={`The themes that are weighed when calculating ${label} for a ${isProduct ? 'product' : 'company'}.`}
          />
          <Topics topics={topicsConsidered} />
        </Section>
      )}

      {sources.length > 0 && (
        <Section>
          <SectionHead
            title="Basis for calculation"
            description={
              // "A selection of" reads wrong when a category draws on a
              // single source (e.g. Knowledge infrastructure → CORE
              // database only), so switch to a singular phrasing then
              // (Janne review, Aug 2026).
              sources.length === 1
                ? `One of the data sources behind Upright's ${label} figures.`
                : `A selection of the data sources behind Upright's ${label} figures.`
            }
          />
          <CardGrid>
            {sources.map(source => (
              <SourceCard key={source.name} source={source} />
            ))}
          </CardGrid>
        </Section>
      )}

      <Section>
        <SectionHead
          title="Examples of positive & negative impacts"
          description={`How ${isProduct ? 'companies and their products' : 'a company'} can affect this category — illustrative, not ${entityName}'s own figures.`}
        />
        <ExampleList>
          <ExampleRow
            valence={Valence.POSITIVE}
            explanation={examples.positiveExplanation}
            example={examples.positiveExample}
          />
          <ExampleRow
            valence={Valence.NEGATIVE}
            explanation={examples.negativeExplanation}
            example={examples.negativeExample}
          />
        </ExampleList>
      </Section>
    </Root>
  );
}

/** Light-weight documentation section header: a bold title with a muted
 *  one-line description beneath, no underline rule (Janne review, Aug 2026
 *  asked first to lighten the subheaders, then to make them bold again).
 *  This is intentionally its own header rather than `DrillInSectionHeader`,
 *  which the data tabs use with a bold title on a gray rule. */
function SectionHead({ title, description }: { title: string; description: string }) {
  return (
    <SectionHeadRoot>
      <SectionTitle>{title}</SectionTitle>
      <SectionDescription>{description}</SectionDescription>
    </SectionHeadRoot>
  );
}

/** A single "topic" reads as prose (a sentence/definition) rather than a
 *  keyword tag when it ends a sentence or is simply too long to be a tag.
 *  Calibrated against the real `topicsConsidered` data (Aug 2026): the
 *  longest genuine tag is 8 words with no sentence period ("Societal
 *  discussions, freedom of media and evidence-based worldview"), whereas the
 *  prose items are either full sentences (Jobs, Scarce human capital) or
 *  long clauses/definitions (Waste's 17-word clause, Scarce natural
 *  resources' "Critical raw materials: …" definition). The 12-word cutoff
 *  sits comfortably between the two so tags never trip it. */
function looksLikeProse(topic: string): boolean {
  const trimmed = topic.trim();
  // A period/!/? anywhere but the very end signals a real sentence break; a
  // single trailing period would also count. Commas and colons alone do NOT
  // — many tags are comma lists ("Racial, economic and gender equality …").
  const hasSentencePunctuation = /[.!?](\s|$)/.test(trimmed);
  const wordCount = trimmed.split(/\s+/).length;
  return hasSentencePunctuation || wordCount > 12;
}

/** Renders a category's topics in the shape that fits their content. Short
 *  keyword tags → light-blue chips. Prose (any item is a sentence or a long
 *  clause) → a plain paragraph for a lone item, or bulleted sentences for
 *  several — a chip is the wrong container for a paragraph regardless of its
 *  border radius (Janne review, Aug 2026). The decision is per category: if
 *  ANY item reads as prose, the whole list renders as prose, so a mixed list
 *  (e.g. Waste's long clause + short "hazardous waste" tag) stays visually
 *  coherent rather than half-chips, half-text. */
function Topics({ topics }: { topics: readonly string[] }) {
  if (!topics.some(looksLikeProse)) {
    return (
      <TopicList>
        {topics.map(topic => (
          <TopicItem key={topic}>{topic}</TopicItem>
        ))}
      </TopicList>
    );
  }
  if (topics.length === 1) {
    return <TopicProse>{topics[0]}</TopicProse>;
  }
  return (
    <TopicProseList>
      {topics.map(topic => (
        <li key={topic}>{topic}</li>
      ))}
    </TopicProseList>
  );
}

/** One data source rendered with the shared card vocabulary: a bordered
 *  card with a tinted icon chip stacked above the source name and the
 *  group as a small caption beneath. The icon is the source-specific glyph
 *  when one is mapped, otherwise the group's glyph. A link card (source has
 *  a URL) opens the source in a new tab; a plain card otherwise (e.g.
 *  "Company reports", no URL). No trailing arrow — the two columns stay
 *  compact and consistent with the More-information cards. */
function SourceCard({ source }: { source: ImpactCategoryDataSource }) {
  const Icon = SOURCE_ICON[source.name] ?? GROUP_ICON[source.group];
  const body = (
    <>
      <IconChip>
        <Icon width={16} height={16} aria-hidden="true" />
      </IconChip>
      <CardText>
        <CardTitle>{source.name}</CardTitle>
        <CardCaption>{source.group}</CardCaption>
      </CardText>
    </>
  );
  if (!source.link) {
    return <CardStatic>{body}</CardStatic>;
  }
  return (
    <CardLink href={source.link} target="_blank" rel="noreferrer">
      {body}
    </CardLink>
  );
}

/** One valence's explanation + optional simplified example, rendered as a
 *  32px valence-tinted icon square + text body — the same row vocabulary
 *  the Everyday-units tab uses for its valenced figures (teal for
 *  positive, purple for negative). Falls back to the modal's "no
 *  {valence} impacts are considered" copy when the category has no
 *  explanation on this side. */
function ExampleRow({ valence, explanation, example }: { valence: Valence; explanation?: string; example?: string }) {
  const isPositive = valence === Valence.POSITIVE;
  const label = isPositive ? 'Positive' : 'Negative';
  return (
    <ExampleRowRoot>
      <IconSquare $valence={valence}>
        {isPositive ? <CheckStreamlineCoreLine width={16} height={16} /> : <XCircle width={16} height={16} />}
      </IconSquare>
      <ExampleBody>
        <ExampleLabel>{label}</ExampleLabel>
        {explanation ? (
          <ExampleText>
            {explanation}
            {example && <ExampleDetail>Simplified example: {formatExample(example)}</ExampleDetail>}
          </ExampleText>
        ) : (
          <ExampleText>No {label.toLowerCase()} impacts are considered for this impact category.</ExampleText>
        )}
      </ExampleBody>
    </ExampleRowRoot>
  );
}

/** Strip the "Example:" prefix and lowercase the first fragment — mirrors
 *  the legacy `formatImpactCategoryExample` so the wording stays identical
 *  to the modal. */
function formatExample(text: string): string {
  return text.toLowerCase().replace('example:', '').trim();
}

/* ─── styled ─────────────────────────────────────────────────────────── */

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xl);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
`;

const SectionHeadRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* Section title: bold, no underline rule. Janne first asked to lighten
   these subheaders (semibold), then in a later Aug 2026 review asked to
   make them bolder again — so they are bold now, still without the data
   tabs' underline rule. */
const SectionTitle = styled.h3`
  margin: 0;
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  line-height: 1.3;
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: var(--font-size__sm);
  color: var(--color__gray-600);
  line-height: 1.5;
  text-wrap: pretty;
`;

/* Topics as light-blue chips with generous padding (Janne review, Aug
   2026 asked for a light-blue/purple fill + more padding). blue-50 fill,
   blue-800 text — a calm, legible tag rather than the earlier flat gray
   pill. */
const TopicList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space__sm);
`;

const TopicItem = styled.li`
  display: inline-flex;
  align-items: center;
  padding: var(--space__xs) var(--space__md);
  /* A capped radius, NOT \`--border-radius__full\`: some categories carry a
     long, multi-sentence "topic" that wraps to several lines, and a full
     (pill) radius turns those tall chips into balloon shapes with huge
     circular ends (Janne review, Aug 2026). \`lg\` keeps short one-line
     chips looking rounded while tall wrapped ones stay a tidy rounded
     rectangle. */
  border-radius: var(--border-radius__lg);
  background: var(--color__blue-50);
  font-size: var(--font-size__sm);
  color: var(--color__blue-800);
  line-height: 1.4;
`;

/* Prose "topics" (a lone sentence/definition) render as a plain paragraph
   in the same muted body voice as the section descriptions — NOT a chip,
   which balloons around a paragraph (Janne review, Aug 2026). */
const TopicProse = styled.p`
  margin: 0;
  font-size: var(--font-size__sm);
  color: var(--color__gray-800);
  line-height: 1.5;
  text-wrap: pretty;
`;

/* Several prose topics render as a compact bulleted list rather than a wall
   of chips — each sentence gets its own line. */
const TopicProseList = styled.ul`
  margin: 0;
  padding: 0 0 0 var(--space__lg);
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
  font-size: var(--font-size__sm);
  color: var(--color__gray-800);
  line-height: 1.5;

  li {
    text-wrap: pretty;
  }
`;

/* Both reference sections lay their cards out in the same two-column grid
   so Basis for calculation and More information read as one family (Janne
   review, Aug 2026). The panel content area is only ~390–470px wide, so
   the column min is small enough that two genuinely fit; `auto-fill` still
   drops to a single column on the narrowest panels. */
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: var(--space__sm);
`;

/* Bordered card (Janne review, Aug 2026: after comparing a borderless
   divider list against bordered cards, picked bordered "for both"). The
   icon chip is stacked above the text so each card fills its vertical space
   and the two columns stay compact — no trailing arrow. Shared surface for
   BOTH the source cards and the KB link cards, so the two sections are one
   consistent family. */
const cardSurface = `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space__xs);
  padding: var(--space__md);
  border: 1px solid var(--color__gray-200);
  border-radius: var(--border-radius__lg);
  background: var(--color__white);
`;

const CardStatic = styled.div`
  ${cardSurface}
`;

const CardLink = styled.a`
  ${cardSurface}
  color: inherit;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  /* Keep the whole card underline-free in every state. The card is the
     click target, not a run of link text, so the browser's default
     hover underline on <a> reads as a stray line under the title (Janne
     review, Aug 2026). Declared on the anchor and its descendants across
     link/hover/focus so no global anchor rule can re-introduce it. */
  &,
  &:hover,
  &:focus,
  & * {
    text-decoration: none;
  }

  &:hover {
    border-color: var(--color__blue-300);
    background: var(--color__blue-50);
  }

  &:hover .card-title {
    color: var(--color__blue-700);
  }
`;

/* Tinted icon chip that leads every card — blue-50 fill, blue-700 glyph.
   Reused by both source cards and KB link cards for one visual language. */
const IconChip = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius__md);
  background: var(--color__blue-50);
  color: var(--color__blue-700);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CardText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const CardTitle = styled.span.attrs({ className: 'card-title' })`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-900);
  line-height: 1.35;
  transition: color 0.15s ease;
`;

const CardCaption = styled.span`
  font-size: var(--font-size__xs);
  color: var(--color__gray-500);
  line-height: 1.3;
`;

const ExampleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
`;

/* Row layout mirrors the Everyday-units tab's figure row: a 32px valence-
   tinted icon square on the left, text body on the right. No card
   background/border — the sibling tabs don't card their valenced rows,
   so neither does this one. */
const ExampleRowRoot = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: var(--space__md);
  align-items: flex-start;
`;

/* 32px tinted square holding a valence glyph. Unlike everyday-units.tsx —
   whose squares hold FILLED streamline-light icons — the glyphs here
   (`CheckStreamlineCoreLine`, `XCircle`) are stroke-only line icons, so
   this square draws with `stroke` and explicitly `fill: none`. Forcing a
   fill onto the open checkmark path renders it as a solid blob (the
   "broken plus" Janne flagged, Aug 2026). */
const IconSquare = styled.div<{ $valence: Valence }>`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius__sm);
  background: ${p => (p.$valence === Valence.POSITIVE ? 'var(--color__teal-100)' : 'var(--color__purple-100)')};
  color: ${p => (p.$valence === Valence.POSITIVE ? 'var(--color__teal-700)' : 'var(--color__purple-700)')};
  flex-shrink: 0;
  svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
  }
`;

const ExampleBody = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ExampleLabel = styled.div`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
`;

const ExampleText = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-800);
  line-height: 1.5;
`;

const ExampleDetail = styled.div`
  margin-top: var(--space__xs);
  color: var(--color__gray-700);
`;
